import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
// Import your logo image
import logoImage from '../../../assets/images/winnestlogo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);

  const navigationItems = [
    {
      name: 'About Us',
      href: '/about',
      hasDropdown: true,
      active: location.pathname.startsWith('/about'),
      dropdownItems: [
        { name: 'Our Profile', href: '/about/profile' },
        { name: 'Our Team', href: '/about/team' },
        { name: 'Our Culture', href: '/about/culture' },
        { name: 'Certificates', href: '/about/certificate' },
        { name: 'Contact', href: '/about/contact' },

        
        
      ]
    },
    {
      name: 'Our Approach',
      href: '/about',
      hasDropdown: true,
      active: location.pathname.startsWith('/about'),
      dropdownItems: [
        
        { name: 'Our Approach', href: '/ourapproach' },
        { name: 'Our Principles', href: '/principles' },
        { name: 'Our Stratergies', href: '/stratergies' },
        { name: 'Our Services', href: '/service' }
       
      ]
    },
    {
      name: 'Invest Zone',
      href: '/investzone',
      hasDropdown: true,
      active: location.pathname.startsWith('/investzone'),
      dropdownItems: [
        { name: 'Award gallery', href: '/investzone/awardgallery' },
        { name: 'Video Tutorial', href: '/investzone/videotutorial' },
        { name: 'KYC', href: '/investzone/kyc' },
        { name: 'Invester Awareness Program', href: '/investzone/investerawareness' },
        { name: 'Financial Calculators', href: '/investzone/financialcalculator' }
      ]
    },
    { name: 'Blogs', href: '/blogs' },
    { name: 'Testimonial', href: '/testimonial', active: location.pathname === '/testimonial' },
    { name: 'Risk Profile', href: '/riskprofile', active: location.pathname === '/riskprofile' },
    { 
      name: 'Customer Login', 
      href: 'https://wealthnest.themfbox.com/', 
      isButton: true,
      isExternal: true 
    }
  ];

  // Handle screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width < 1024);
      
      // Close mobile menu when switching to desktop
      if (width >= 1024) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    // Initial check
    updateScreenSize();

    // Add event listener
    window.addEventListener('resize', updateScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Handle contact clicks
  const handleEmailClick = useCallback((event) => {
    event.preventDefault();
    window.location.href = 'mailto:vcare@wealthnest.in';
  }, []);

  const handlePhoneClick = useCallback((event) => {
    event.preventDefault();
    window.location.href = 'tel:+919036024568';
  }, []);

  // Handle dropdown toggle
  const handleDropdownToggle = useCallback((itemName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveDropdown(prev => prev === itemName ? null : itemName);
  }, []);

  // Check if URL is external
  const isExternalUrl = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  // Navigation handler - Updated to handle external URLs
  const handleNavigation = useCallback((href, isExternal = false, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!href) return;

    // Close all menus
    setIsMenuOpen(false);
    setActiveDropdown(null);

    // Handle external URLs - navigate in same tab for seamless experience
    if (isExternal || isExternalUrl(href)) {
      // Navigate to external URL in same tab
      window.location.href = href;
      return;
    }

    // Handle internal navigation
    if (href !== location.pathname) {
      navigate(href);
    }

    // Scroll to top for internal navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, [navigate, location.pathname]);

  // Handle mobile menu toggle
  const handleMobileMenuToggle = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsMenuOpen(prev => !prev);
    setActiveDropdown(null);
  }, []);

  // Handle outside clicks and cleanup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
        if (isMobile) {
          setIsMenuOpen(false);
        }
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobile]);

  // Close menus when route changes
  useEffect(() => {
    setActiveDropdown(null);
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top Contact Bar */}
      <div style={{
        backgroundColor: '#1a2332',
        color: '#ffffff',
        fontSize: screenWidth < 480 ? '0.75rem' : screenWidth < 768 ? '0.8rem' : '0.875rem',
        padding: screenWidth < 480 ? '4px 0' : '8px 0',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: screenWidth < 360 ? 'none' : 'block'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: `0 ${screenWidth < 480 ? '0.75rem' : screenWidth < 768 ? '1rem' : '1.5rem'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: screenWidth < 480 ? '0.5rem' : '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: screenWidth < 480 ? '0.5rem' : screenWidth < 768 ? '1rem' : '2rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleEmailClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ffffff',
                fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
                fontWeight: '400',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = '#22c55e';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ffffff';
              }}
              title="Send us an email"
            >
              <Mail size={screenWidth < 768 ? 12 : 14} />
              <span>vcare@wealthnest.in</span>
            </button>
            
            <button
              onClick={handlePhoneClick}
              style={{
                display: screenWidth < 480 ? 'none' : 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ffffff',
                fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
                fontWeight: '400',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = '#22c55e';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ffffff';
              }}
              title="Call us"
            >
              <Phone size={screenWidth < 768 ? 12 : 14} />
              <span>
                {screenWidth < 640 ? '+91 9036024568' : '+91 9036024568 / +91 9036025568'}
              </span>
            </button>
          </div>
          <div style={{
            fontWeight: '500',
            fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
            color: '#22c55e',
            textAlign: 'center',
            display: screenWidth < 640 ? 'none' : 'block'
          }}>
            AMFI Registered Mutual Fund Distributor
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav 
        ref={headerRef}
        style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: `0 ${screenWidth < 480 ? '0.75rem' : screenWidth < 768 ? '1rem' : '2rem'}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: screenWidth < 480 ? '60px' : screenWidth < 768 ? '65px' : '70px',
            minHeight: '60px'
          }}>
            {/* Logo Section */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              maxWidth: isMobile ? 'calc(100% - 60px)' : 'auto'
            }}>
              <button
                onClick={(e) => handleNavigation('/', false, e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screenWidth < 480 ? '0.25rem' : '0.5rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screenWidth < 480 ? '0.25rem' : '0.5rem',
                  minWidth: 0,
                  width: '100%'
                }}>
                  {/* WinNest Logo Image */}
                  <img 
                    src={logoImage} 
                    alt="WinNest Logo" 
                    style={{
                      width: screenWidth < 480 ? '35px' : screenWidth < 768 ? '40px' : '50px',
                      height: screenWidth < 480 ? '35px' : screenWidth < 768 ? '40px' : '50px',
                      objectFit: 'contain',
                      flexShrink: 0
                    }}
                  />
                  
                  <div style={{ 
                    textAlign: 'left',
                    minWidth: 0,
                    overflow: 'hidden',
                    flex: 1
                  }}>
                    {/* Updated WinNest Text with styled colors */}
                    <div style={{
                      fontSize: screenWidth < 380 ? '14px' : screenWidth < 480 ? '16px' : screenWidth < 768 ? '18px' : '20px',
                      fontWeight: '600',
                      lineHeight: '1.1',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      <span style={{ color: '#333333' }}>Win</span>
                      <span style={{ color: '#22c55e' }}>Nest Pvt.Ltd.</span>
                    </div>
                    <div style={{
                      fontSize: screenWidth < 380 ? '8px' : screenWidth < 480 ? '9px' : screenWidth < 768 ? '10px' : '11px',
                      color: '#666666',
                      fontWeight: '400',
                      marginTop: '1px',
                      letterSpacing: '0.3px',
                      display: screenWidth < 380 ? 'none' : 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      Prosperity Is Your Birth-Right Attain It..
                    </div>
                    <div style={{
                      fontSize: screenWidth < 480 ? '7px' : screenWidth < 768 ? '9px' : '10px',
                      color: '#22c55e',
                      fontWeight: '500',
                      marginTop: '1px',
                      letterSpacing: '0.2px',
                      display: screenWidth < 480 ? 'none' : 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      AMFI Registered Mutual Fund Distributor
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Desktop Navigation */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: screenWidth < 1200 ? '1rem' : '2rem'
              }}>
                {navigationItems.map((item) => (
                  <div key={item.name} style={{ position: 'relative' }}>
                    {item.isButton ? (
                      <button
                        onClick={(e) => handleNavigation(item.href, item.isExternal, e)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#22c55e',
                          padding: '0.5rem 1rem',
                          borderRadius: '25px',
                          border: '2px solid #22c55e',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: screenWidth < 1200 ? '12px' : '14px',
                          letterSpacing: '0.3px',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#22c55e';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#22c55e';
                        }}
                      >
                        Customer Login →
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={item.hasDropdown ? 
                            (e) => handleDropdownToggle(item.name, e) : 
                            (e) => handleNavigation(item.href, item.isExternal, e)
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.5rem 0',
                            fontWeight: '500',
                            fontSize: screenWidth < 1200 ? '13px' : '15px',
                            color: item.active ? '#22c55e' : '#333333',
                            backgroundColor: 'transparent',
                            border: 'none',
                            transition: 'color 0.2s ease',
                            cursor: 'pointer',
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#22c55e';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = item.active ? '#22c55e' : '#333333';
                          }}
                        >
                          <span>{item.name}</span>
                          {item.hasDropdown && (
                            <ChevronDown 
                              size={14} 
                              style={{
                                transform: activeDropdown === item.name ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                              }} 
                            />
                          )}
                        </button>

                        {/* Desktop Dropdown */}
                        {item.hasDropdown && activeDropdown === item.name && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              minWidth: item.name === 'Invest Zone' ? '400px' : '200px',
                              backgroundColor: 'white',
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
                              borderRadius: '8px',
                              padding: item.name === 'Invest Zone' ? '1.5rem' : '0.75rem',
                              zIndex: 1000,
                              border: '1px solid #f0f0f0',
                              marginTop: '0.5rem'
                            }}
                          >
                            {item.name === 'Invest Zone' ? (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: screenWidth < 1300 ? '1fr' : 'repeat(2, 1fr)',
                                gap: '0.75rem'
                              }}>
                                {item.dropdownItems.map((dropItem) => {
                                  const iconMap = {
                                    'Award gallery': '🏆',
                                    'Video Tutorial': '🎥',
                                    'Schedule a Meeting': '📅',
                                    'KYC': '👤',
                                    'Invester Awareness Program': '🧠',
                                    'Financial Calculators': '🧮'
                                  };

                                  return (
                                    <button
                                      key={dropItem.name}
                                      onClick={(e) => handleNavigation(dropItem.href, dropItem.isExternal, e)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '1rem',
                                        backgroundColor: 'white',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        width: '100%'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#f0fdf4';
                                        e.target.style.borderColor = '#22c55e';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'white';
                                        e.target.style.borderColor = '#f0f0f0';
                                      }}
                                    >
                                      <div style={{
                                        fontSize: '1.5rem',
                                        minWidth: '30px',
                                        textAlign: 'center'
                                      }}>
                                        {iconMap[dropItem.name] || '📊'}
                                      </div>
                                      <div style={{
                                        fontWeight: '500',
                                        color: '#333333',
                                        fontSize: '14px',
                                        lineHeight: '1.3'
                                      }}>
                                        {dropItem.name}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                {item.dropdownItems.map((dropItem) => (
                                  <button
                                    key={dropItem.name}
                                    onClick={(e) => handleNavigation(dropItem.href, dropItem.isExternal, e)}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '0.75rem 1rem',
                                      color: '#333333',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s ease',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                      borderRadius: '6px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#f0fdf4';
                                      e.target.style.color = '#22c55e';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = '#333333';
                                    }}
                                  >
                                    {dropItem.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <button
                onClick={handleMobileMenuToggle}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: screenWidth < 480 ? '35px' : '40px',
                  height: screenWidth < 480 ? '35px' : '40px',
                  background: 'none',
                  border: '2px solid #f0f0f0',
                  borderRadius: '6px',
                  color: '#333333',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#22c55e';
                  e.target.style.color = '#22c55e';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#f0f0f0';
                  e.target.style.color = '#333333';
                }}
              >
                {isMenuOpen ? 
                  <X size={screenWidth < 480 ? 16 : 18} /> : 
                  <Menu size={screenWidth < 480 ? 16 : 18} />
                }
              </button>
            )}
          </div>

          {/* Mobile Navigation */}
          {isMobile && isMenuOpen && (
            <div style={{
              backgroundColor: 'white',
              borderTop: '1px solid #f0f0f0',
              padding: '1rem 0',
              display: 'block',
              maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {/* Mobile Contact Information */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#1a2332',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <button
                    onClick={handleEmailClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '400',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      width: '100%',
                      marginBottom: '0.5rem',
                      transition: 'all 0.2s ease'
                    }}
                    onTouchStart={(e) => {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.target.style.backgroundColor = 'transparent';
                      }, 150);
                    }}
                  >
                    <Mail size={16} />
                    <span>info@winnest.in</span>
                  </button>
                  
                  <button
                    onClick={handlePhoneClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '400',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      width: '100%',
                      transition: 'all 0.2s ease'
                    }}
                    onTouchStart={(e) => {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.target.style.backgroundColor = 'transparent';
                      }, 150);
                    }}
                  >
                    <Phone size={16} />
                    <span>+91 9036024568</span>
                  </button>
                </div>

                {navigationItems.map((item) => (
                  <div key={item.name} style={{ width: '100%' }}>
                    {item.isButton ? (
                      <button
                        onClick={(e) => handleNavigation(item.href, item.isExternal, e)}
                        style={{
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: '#22c55e',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '2px solid #22c55e',
                          fontWeight: '600',
                          marginTop: '1rem',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={item.hasDropdown ? 
                            (e) => handleDropdownToggle(item.name, e) : 
                            (e) => handleNavigation(item.href, item.isExternal, e)
                          }
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.75rem 1rem',
                            borderRadius: '6px',
                            backgroundColor: item.active ? '#f0fdf4' : 'transparent',
                            border: 'none',
                            fontWeight: '500',
                            fontSize: '15px',
                            color: item.active ? '#22c55e' : '#333333',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <span style={{ textAlign: 'left', flex: 1 }}>
                            {item.name}
                          </span>
                          {item.hasDropdown && (
                            <ChevronDown 
                              size={14} 
                              style={{
                                transform: activeDropdown === item.name ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                              }} 
                            />
                          )}
                        </button>

                        {/* Mobile Dropdown */}
                        {item.hasDropdown && activeDropdown === item.name && (
                          <div 
                            style={{
                              marginLeft: '1rem',
                              marginTop: '0.5rem',
                              paddingLeft: '1rem',
                              borderLeft: '2px solid #f0f0f0'
                            }}
                          >
                            {item.dropdownItems.map((dropItem) => (
                              <button
                                key={dropItem.name}
                                onClick={(e) => handleNavigation(dropItem.href, dropItem.isExternal, e)}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#666666',
                                  borderRadius: '4px',
                                  marginBottom: '0.25rem',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  transition: 'all 0.2s ease'
                                }}
                                onTouchStart={(e) => {
                                  e.target.style.color = '#22c55e';
                                  e.target.style.backgroundColor = '#f0fdf4';
                                }}
                                onTouchEnd={(e) => {
                                  setTimeout(() => {
                                    e.target.style.color = '#666666';
                                    e.target.style.backgroundColor = 'transparent';
                                  }, 150);
                                }}
                              >
                                {dropItem.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Header;